import { on } from '@ngrx/store';
import { ManagementBaseActions, AsyncActions } from './management-base.actions';
import { OperatorLocksActions } from '../emergency-items/operator-locks.actions';
import { ItemGroupingsActions } from '../item-groupings/item-groupings.actions';
import { ItemsActions } from '../items/items.actions';
import { ItemTypesActions } from '../item-types/item-types.actions';
import { LocationSetsActions } from '../location-sets/location-sets.actions';
import { LocationSetTypesActions } from '../location-set-types/location-set-types.actions';
import {asyncActionState, AsyncActionState} from '@emm/core/util/async-action-state';
import { Attribute } from '@emm/core/models/attribute.model';
import {Item} from '@emm/core/models/item.model';
import {ItemLocaleResponse, Locale} from '@emm/core/models/locale.model';
import {ItemType} from '@emm/core/models/item-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {Revotes} from '@emm/core/models/revotes.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {withLoadables} from '@emm/core/util/ngrx.util';
import {Screen} from '@emm/core/models/screen.model';




export const baseFeatureKey = 'base';

export interface BaseState extends AsyncActionState {
    attributeDetail: Attribute;
    emergencyItemDetail: { tag: string };
    initialized: boolean;
    itemDetail: Item;
    itemLocaleDetail: ItemLocaleResponse;
    itemGroupingDetail: Item;
    itemTypeDetail: ItemType;
    locationSetDetail: LocationSet;
    locationSetTypeDetail: LocationSetType;
    revoteDetails: Revotes;
    screens: Screen[];
    selectedCountry: Locale;
    sidenavOpened: boolean;
    languageAssignmentDetails: any;
    locations: LocationSet[];
    selected: any;
    selections: any;
    templateOptionsByLocation: any;
    templateByLocation: any;
    searchFilter: SearchFilter;
}

const initialState: BaseState = {
    attributeDetail: null,
    emergencyItemDetail: null,
    initialized: false,
    itemDetail: null,
    itemLocaleDetail: [],
    itemGroupingDetail: null,
    itemTypeDetail: null,
    locationSetDetail: null,
    locationSetTypeDetail: null,
    screens: [],
    revoteDetails: null,
    selectedCountry: null,
    sidenavOpened: true,
    languageAssignmentDetails: null,
    locations: [],
    selected: {},
    selections: {},
    templateOptionsByLocation: [],
    templateByLocation: [],
    searchFilter: null,
    ...asyncActionState.initial
};

const { loadActions, successActions, errorActions } = AsyncActions;

export const managementBaseReducer = withLoadables<BaseState>({
    loadActions,
    successActions,
    errorActions
})(
    initialState,
    on(ManagementBaseActions.initSuccess, (state: BaseState, { screens }) => ({
        ...state,
        initialized: true,
        screens: [...screens]
    })),
    on(ManagementBaseActions.changeScreen, (state: BaseState, { screen }) => ({
        ...state,
        screens: state.screens.map((item) => ({ ...item, selected: screen.name === item.name }))
    })),
    on(ManagementBaseActions.selectCountry, (state: BaseState, { country }) => ({
        ...state,
        selectedCountry: { ...country }
    })),
    on(
        ManagementBaseActions.loadItemDetail,
        ManagementBaseActions.initItems,
        ItemsActions.deleteSuccess,
        ItemGroupingsActions.removeChildItemSuccess,
        ItemGroupingsActions.deleteItemGroupSuccess,
        (state: BaseState, action) => ({
            ...state,
            itemDetail: null
        })
    ),
    on(
        ManagementBaseActions.loadItemDetailSuccess,
        ManagementBaseActions.loadItemGroupingDetailSuccess,
        (state: BaseState, action) => (
            {
                ...state,
                itemDetail: { ...action.item }
            }
        )
    ),
    on(
        ManagementBaseActions.loadItemLocaleDetail,
        (state: BaseState, action) => (
            {
                ...state
            }
        )
    ),
    on(
        ManagementBaseActions.loadItemLocaleDetailSuccess,
        (state: BaseState, action) => (
            {
                ...state,
                itemLocaleDetail: [...action.itemLocale]
            }
        )
    ),
    on(
        ManagementBaseActions.updateItemSuccess,
        ManagementBaseActions.updateItemGroupingSuccess,
        (state: BaseState, action) =>
        action.dispatchReload
            ? {
                ...state,
                itemDetail: { ...action.item }
            }
            : state
    ),
    on(
        ManagementBaseActions.loadItemTypeDetail,
        ManagementBaseActions.initItemTypes,
        ItemTypesActions.deleteSuccess,
        ManagementBaseActions.loadItemGroupingDetail,
        ManagementBaseActions.initItemGroupings,
        (state: BaseState, action) => ({
            ...state,
            itemDetail: null,
            itemTypeDetail: null
        })
    ),
    on(ManagementBaseActions.loadItemTypeDetailSuccess, (state: BaseState, action) => ({
        ...state,
        itemTypeDetail: { ...action.itemType }
    })),
    on(ManagementBaseActions.updateItemTypeSuccess, (state: BaseState, action) =>
        action.dispatchReload
            ? {
                ...state,
                itemTypeDetail: { ...action.itemType }
            }
            : state
    ),
    on(
        ManagementBaseActions.loadLocationSetTypeDetail,
        ManagementBaseActions.initLocationSetTypes,
        LocationSetTypesActions.deleteSuccess,
        (state: BaseState, action) => ({
            ...state,
            locationSetTypeDetail: null
        })
    ),
    on(ManagementBaseActions.loadLocationSetTypeDetailSuccess, (state: BaseState, action) => ({
        ...state,
        locationSetTypeDetail: { ...action.locationSetType }
    })),
    on(ManagementBaseActions.updateLocationSetTypeSuccess, (state: BaseState, action) =>
        action.dispatchReload
            ? {
                ...state,
                locationSetTypeDetail: { ...action.locationSetType }
            }
            : state
    ),
    on(ManagementBaseActions.loadAttributeDetail, (state: BaseState, action) => ({
        ...state,
        attributeDetail: null
    })),
    on(ManagementBaseActions.loadAttributeDetailSuccess, (state: BaseState, action) => ({
        ...state,
        attributeDetail: { ...action.attribute }
    })),
    on(OperatorLocksActions.loadAllLocationStatuses, (state: BaseState, action) => ({
        ...state,
        emergencyItemDetail: null
    })),
    on(ManagementBaseActions.selectEmergencyItemDetail, (state: BaseState, action) => ({
        ...state,
        emergencyItemDetail: { ...action.item }
    })),
    on(
        ManagementBaseActions.initLocationSets,
        LocationSetsActions.deleteLocationSetSuccess,
        LocationSetsActions.removeChildLocationSuccess,
        (state: BaseState, action) => ({
            ...state,
            locationSetDetail: null
        })
    ),
    on(LocationSetsActions.select, (state: BaseState, action) => ({
        ...state,
        locationSetDetail: { ...action.locationSet }
    })),
    on(ManagementBaseActions.updateLocationSetSuccess, (state: BaseState, action) =>
        action.dispatchReload
            ? {
                ...state,
                locationSetDetail: { ...action.locationSet }
            }
            : state
    ),
    on(
        ManagementBaseActions.initMasterMenuSystem,
        ManagementBaseActions.loadRevoteDetail,
        (state: BaseState, action) => ({
            ...state,
            revoteDetails: null
        })
    ),
    on(
        ManagementBaseActions.loadRevoteDetailSuccess,
        (state: BaseState, action) => ({
            ...state,
            revoteDetails: { ...action.revote }
        })
    ),
    on(ManagementBaseActions.updateRevoteSuccess, (state: BaseState, action) =>
        action.dispatchReload
            ? {
                ...state,
                revoteDetails: { ...action.revote }
            }
            : state
    ),
    on(
        ManagementBaseActions.initMmsAdmin,
        (state: BaseState, action) => ({
            ...state
        })
    ),
    on(ManagementBaseActions.searchLocation, (state: BaseState, { filter }) => ({
        ...state,
        searchFilter: filter,
    })),
    on(
        ManagementBaseActions.initLanguageAssignments,
        ManagementBaseActions.loadAssignmentDetail,
        (state: BaseState, action) => ({
            ...state
        })
    ),
    on(ManagementBaseActions.loadAllLocationSuccess, (state: BaseState, { locationSets }) => ({
        ...state,
        locations: locationSets,
    })),
    on(ManagementBaseActions.startLoading, (state: BaseState) => ({
        ...state,
        ...asyncActionState.loading
    })),
    on(ManagementBaseActions.loadingSuccess, (state: BaseState) => ({
        ...state,
        ...asyncActionState.success
    })),
    on(ManagementBaseActions.loadingFailure, (state: BaseState) => ({
        ...state,
        ...asyncActionState.error
    }))
);
